import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Loader2, User, Bot, Headphones } from "lucide-react";
import axios from "axios";
import sounds from "@/utils/sounds";
import { useLanguage } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LiveChat = () => {
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState("ai"); // "ai" or "human"
  const [sessionId] = useState(() => `chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const [humanChatId, setHumanChatId] = useState(null);
  const [humanMessages, setHumanMessages] = useState([]);
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: "bot",
      text: "Hello! 👋 Welcome to Shylov Cleaning. I'm your AI assistant and can help you with questions about our yacht, residential, and construction cleaning services on the French Riviera. How can I help you today?",
      time: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, humanMessages]);

  // Load existing human chat
  useEffect(() => {
    try {
      const savedChatId = localStorage.getItem("activeChatId");
      if (savedChatId) setHumanChatId(savedChatId);
    } catch { /* Chat preferences must not prevent the page from loading. */ }
  }, []);

  // Poll for human chat messages
  useEffect(() => {
    let interval;
    if (isOpen && mode === "human" && humanChatId) {
      loadHumanMessages(humanChatId);
      interval = setInterval(() => loadHumanMessages(humanChatId, true), 5000);
    }
    return () => clearInterval(interval);
  }, [isOpen, mode, humanChatId]);

  const loadHumanMessages = async (chatId, silent = false) => {
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/chat/${chatId}/messages`, { headers });
      setHumanMessages(res.data.messages || []);
    } catch (error) {
      if (error.response?.status === 404) {
        try { localStorage.removeItem("activeChatId"); } catch {}
        setHumanChatId(null);
        setMode("ai");
      }
    }
  };

  const startHumanChat = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.post(`${API}/chat/create`, {
        subject: "Support Request",
        initial_message: "Hello, I'd like to speak with an operator."
      }, { headers });
      
      const chatId = res.data.chat_id;
      setHumanChatId(chatId);
      try { localStorage.setItem("activeChatId", chatId); } catch {}
      setMode("human");
      await loadHumanMessages(chatId);
    } catch (error) {
      console.error("Failed to start human chat:", error);
    }
  };

  const sendHumanMessage = async () => {
    if (!inputValue.trim() || !humanChatId) return;
    
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/chat/${humanChatId}/message`, {
        content: inputValue.trim()
      }, { headers });
      
      setInputValue("");
      await loadHumanMessages(humanChatId, true);
    } catch (error) {
      console.error("Failed to send message:", error);
    }
  };

  // Play notification sound when new message arrives
  useEffect(() => {
    if (messages.length > 1 && messages[messages.length - 1].type === "bot") {
      sounds.notification();
    }
  }, [messages]);

  const quickReplies = [
    "📅 Book a cleaning",
    "💰 Get a quote",
    "🕐 Working hours",
    "📍 Service areas"
  ];

  const handleSend = async (text = inputValue) => {
    if (!text.trim()) return;
    
    sounds.click();
    
    // Add user message
    const userMessage = {
      id: Date.now(),
      type: "user",
      text: text.trim(),
      time: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setInputValue("");
    
    // Show typing indicator
    setIsTyping(true);
    
    try {
      // Call AI endpoint
      const response = await axios.post(`${API}/chat/ai`, {
        message: text.trim(),
        session_id: sessionId
      });
      
      setIsTyping(false);
      
      const botMessage = {
        id: Date.now() + 1,
        type: "bot",
        text: response.data.response || "Sorry, I couldn't process that. Please try again.",
        time: new Date()
      };
      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      console.error("Chat error:", error);
      setIsTyping(false);
      
      // Fallback response
      const botMessage = {
        id: Date.now() + 1,
        type: "bot",
        text: "I apologize, but I'm having trouble connecting right now. Please contact us directly at +33 6 98 35 88 15 or via WhatsApp. 📞",
        time: new Date()
      };
      setMessages(prev => [...prev, botMessage]);
    }
  };

  const handleQuickReply = (reply) => {
    handleSend(reply);
  };

  return (
    <>
      {/* Chat Button */}
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 1, duration: 0.3 }}
        onClick={() => {
          setIsOpen(!isOpen);
          sounds.click();
        }}
        className={`fixed bottom-24 right-6 z-40 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-colors duration-300 ${
          isOpen ? "bg-slate-600" : "bg-marine-600 hover:bg-marine-700"
        }`}
        data-testid="chat-button"
      >
        {isOpen ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <>
            <MessageCircle className="w-6 h-6 text-white" />
            <span className="absolute top-0 right-0 w-4 h-4 bg-red-500 rounded-full text-[10px] text-white flex items-center justify-center font-bold">
              1
            </span>
          </>
        )}
      </motion.button>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.3 }}
            className="fixed bottom-40 right-6 z-40 w-[360px] max-w-[calc(100vw-48px)] bg-white rounded-2xl shadow-2xl overflow-hidden border border-slate-100"
            data-testid="chat-window"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-navy-900 to-marine-700 text-white p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  {mode === "ai" ? <Bot className="w-6 h-6" /> : <Headphones className="w-6 h-6" />}
                </div>
                <div>
                  <h3 className="font-semibold">{mode === "ai" ? "AI Assistant" : "Live Support"}</h3>
                  <p className="text-xs text-white/70 flex items-center gap-1">
                    <span className="w-2 h-2 bg-green-400 rounded-full" />
                    {mode === "ai" ? "Always available" : "Operator online"}
                  </p>
                </div>
              </div>
            </div>

            {/* Messages */}
            <div className="h-[320px] overflow-y-auto p-4 space-y-4 bg-slate-50">
              {mode === "ai" ? (
                <>
                  {messages.map((message) => (
                    <div
                      key={message.id}
                      className={`flex items-end gap-2 ${message.type === "user" ? "flex-row-reverse" : ""}`}
                    >
                      <div 
                        className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                          message.type === "user" ? "bg-marine-600" : "bg-navy-900"
                        }`}
                      >
                        {message.type === "user" ? (
                          <User className="w-4 h-4 text-white" />
                        ) : (
                          <Bot className="w-4 h-4 text-white" />
                        )}
                      </div>
                      <div 
                        className={`max-w-[75%] p-3 rounded-2xl ${
                          message.type === "user" 
                            ? "bg-marine-600 text-white rounded-br-md" 
                            : "bg-white text-navy-900 shadow-sm rounded-bl-md"
                        }`}
                      >
                        <p className="text-sm leading-relaxed">{message.text}</p>
                        <p className={`text-[10px] mt-1 ${message.type === "user" ? "text-white/60" : "text-slate-400"}`}>
                          {message.time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  ))}
                  
                  {/* Typing indicator */}
                  {isTyping && (
                    <div className="flex items-end gap-2">
                      <div className="w-8 h-8 rounded-full bg-navy-900 flex items-center justify-center">
                        <Bot className="w-4 h-4 text-white" />
                      </div>
                      <div className="bg-white p-3 rounded-2xl rounded-bl-md shadow-sm">
                        <div className="flex gap-1">
                          <span className="w-2 h-2 bg-slate-300 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                          <span className="w-2 h-2 bg-slate-300 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                          <span className="w-2 h-2 bg-slate-300 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                        </div>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <>
                  {humanMessages.length === 0 ? (
                    <div className="text-center text-slate-500 py-8">
                      <Headphones className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                      <p className="text-sm">Connected to support</p>
                      <p className="text-xs text-slate-400">An operator will respond shortly</p>
                    </div>
                  ) : (
                    humanMessages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`flex items-end gap-2 ${msg.sender_type === "user" ? "flex-row-reverse" : ""}`}
                      >
                        <div 
                          className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                            msg.sender_type === "user" ? "bg-marine-600" : "bg-green-600"
                          }`}
                        >
                          {msg.sender_type === "user" ? (
                            <User className="w-4 h-4 text-white" />
                          ) : (
                            <Headphones className="w-4 h-4 text-white" />
                          )}
                        </div>
                        <div 
                          className={`max-w-[75%] p-3 rounded-2xl ${
                            msg.sender_type === "user" 
                              ? "bg-marine-600 text-white rounded-br-md" 
                              : "bg-white text-navy-900 shadow-sm rounded-bl-md"
                          }`}
                        >
                          {msg.sender_type === "admin" && (
                            <p className="text-[10px] text-green-600 font-medium mb-1">{msg.sender_name}</p>
                          )}
                          <p className="text-sm leading-relaxed">{msg.content}</p>
                          <p className={`text-[10px] mt-1 ${msg.sender_type === "user" ? "text-white/60" : "text-slate-400"}`}>
                            {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                </>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {/* Quick Replies */}
            <div className="p-3 bg-white border-t border-slate-100">
              {mode === "ai" && (
                <>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {quickReplies.map((reply) => (
                      <button
                        key={reply}
                        onClick={() => handleQuickReply(reply)}
                        className="text-xs bg-slate-100 hover:bg-slate-200 text-navy-900 px-3 py-1.5 rounded-full transition-colors"
                      >
                        {reply}
                      </button>
                    ))}
                  </div>
                  {/* Talk to Operator Button */}
                  <button
                    onClick={startHumanChat}
                    className="w-full text-xs bg-navy-900 hover:bg-navy-800 text-white px-3 py-2 rounded-lg transition-colors flex items-center justify-center gap-2"
                    data-testid="talk-to-operator-btn"
                  >
                    <Headphones className="w-3 h-3" />
                    Talk to Operator
                  </button>
                </>
              )}
              {mode === "human" && (
                <button
                  onClick={() => setMode("ai")}
                  className="w-full text-xs bg-slate-100 hover:bg-slate-200 text-navy-900 px-3 py-2 rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  <Bot className="w-3 h-3" />
                  Switch to AI Assistant
                </button>
              )}
            </div>

            {/* Input */}
            <div className="p-3 bg-white border-t border-slate-100">
              <form 
                onSubmit={(e) => { 
                  e.preventDefault(); 
                  mode === "ai" ? handleSend() : sendHumanMessage();
                }}
                className="flex items-center gap-2"
              >
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder={mode === "human" ? "Type your message to operator..." : t("forms.typeMessage")}
                  className="flex-1 bg-slate-100 border-0 rounded-full px-4 py-2.5 text-sm focus:ring-2 focus:ring-marine-500"
                  data-testid="chat-input"
                />
                <button
                  type="submit"
                  disabled={!inputValue.trim()}
                  className="w-10 h-10 bg-marine-600 hover:bg-marine-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-full flex items-center justify-center transition-colors"
                  data-testid="chat-send"
                >
                  <Send className="w-4 h-4" />
                </button>
              </form>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default LiveChat;
